import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  BookOpen,
  Lightbulb,
  Edit3,
  FolderOpen,
  X,
  ChevronLeft,
  ChevronRight,
  Save,
  AlertCircle,
  CheckCircle,
  Sparkles,
  FileSearch,
  AlignLeft,
  Type,
  Target,
  Clock,
  Calendar,
  Building,
  Hash,
  Zap,
  Brain,
  MessageSquare,
  PenTool,
  Trash2,
  Wand2,
  Share2,
  RotateCcw
} from 'lucide-react';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { toast } from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import ConversationalEssayBuilder from './ConversationalEssayBuilder';
import EssayRefinementInterface from './EssayRefinementInterface';
import WritingSupportSidebar from './WritingSupport/WritingSupportSidebar';
import ShareButton from '../Sharing/ShareButton';
import DraftLibrary from './Essays/DraftLibrary';
import StrategicWordCountPanel from './EssayRefinement/StrategicWordCountPanel';
import { calculateWordCount, validateChangeThreshold } from '../../services/StrategicWordCountService';
import CollegeNotebook from './CollegeNotebook/CollegeNotebook';

// Selection screen component - moved outside to prevent remounting
const SelectionScreen = ({ handleNewEssay, setSelectedOption, recentWork = [], draftCount = 0 }) => (
  <div className="min-h-screen bg-stone-50 p-8">
    <div className="max-w-[1200px] mx-auto">
      
      {/* Header */}
      <div className="mb-12">
        <h1 className="text-slate-900 font-bold text-2xl mb-2">
          Start Your Essay
        </h1>
        <p className="text-stone-600 text-sm">
          Choose the approach that matches where you are right now
        </p>
      </div>

      {/* Recommended Path - Maximum Visual Weight */}
      <div className="mb-8 bg-white border-l-4 border-l-gold-500 border border-slate-900 rounded-xl overflow-hidden shadow-sm">
        <div className="p-8">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gold-100 rounded-lg flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-gold-600" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-slate-900 font-bold text-lg">
                    Find Ideas
                  </h2>
                </div>
                <p className="text-stone-600 text-sm">
                  Best starting point for most students
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gold-400 rounded-lg">
              <svg className="w-3.5 h-3.5 text-slate-900" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="text-slate-900 text-xs font-bold uppercase tracking-wide">
                Start Here
              </span>
            </div>
          </div>

          <p className="text-slate-700 text-sm mb-6 leading-relaxed">
            Not sure what to write about? Explore prompts and discover compelling topics through AI-powered conversations. Your assessment provides the foundation for generating authentic ideas.
          </p>

          <button 
            onClick={() => setSelectedOption('ideas')}
            className="w-full bg-slate-900 text-white px-6 py-3 rounded-lg font-medium text-sm flex items-center justify-center gap-2 hover:bg-slate-800 transition-colors"
          >
            Start Exploring Ideas
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Alternative Paths - Clean Grid */}
      <div className="grid grid-cols-3 gap-4">
        
        {/* Essay Builder */}
        <button 
          onClick={() => setSelectedOption('builder')}
          className="bg-white border border-slate-300 rounded-lg p-6 hover:border-slate-900 transition-colors text-left group"
        >
          <div className="w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-slate-900 transition-colors">
            <Sparkles className="w-4 h-4 text-slate-600 group-hover:text-white transition-colors" />
          </div>
          <h3 className="text-slate-900 font-bold text-sm mb-2">
            Essay Builder
          </h3>
          <p className="text-stone-600 text-xs leading-relaxed mb-3">
            Step-by-step prompts help you develop your story. Best when you already have a topic.
          </p>
          <span className="text-indigo-600 text-xs font-medium">
            Guided approach →
          </span>
        </button>

        {/* Blank Draft */}
        <button 
          onClick={handleNewEssay}
          className="bg-white border border-slate-300 rounded-lg p-6 hover:border-slate-900 transition-colors text-left group"
        >
          <div className="w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-slate-900 transition-colors">
            <FileText className="w-4 h-4 text-slate-600 group-hover:text-white transition-colors" />
          </div>
          <h3 className="text-slate-900 font-bold text-sm mb-2">
            Blank Draft
          </h3>
          <p className="text-stone-600 text-xs leading-relaxed mb-3">
            Start with a clean page. Full creative freedom when you know what you want to write.
          </p>
          <span className="text-teal-600 text-xs font-medium">
            Direct approach →
          </span>
        </button>

        {/* Continue Draft */}
        <button 
          onClick={() => setSelectedOption('existing')}
          className="bg-white border border-slate-300 rounded-lg p-6 hover:border-slate-900 transition-colors text-left group"
        >
          <div className="w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-slate-900 transition-colors">
            <RotateCcw className="w-4 h-4 text-slate-600 group-hover:text-white transition-colors" />
          </div>
          <h3 className="text-slate-900 font-bold text-sm mb-2">
            Continue Draft
          </h3>
          <p className="text-stone-600 text-xs leading-relaxed mb-3">
            Pick up where you left off. {draftCount} {draftCount === 1 ? 'draft' : 'drafts'} ready to refine or expand.
          </p>
          <span className="text-magenta-600 text-xs font-medium">
            Resume work →
          </span>
        </button>

      </div>

      {/* Context Helper */}
      <div className="mt-6 p-4 bg-white border border-stone-200 rounded-lg">
        <p className="text-stone-600 text-xs">
          <span className="font-medium text-slate-700">Not sure?</span> Start with Find Ideas to discover what makes your story compelling. Once you have a topic, Essay Builder helps you develop it with structure. You can always switch approaches.
        </p>
      </div>

    </div>
  </div>
);

// Writing interface component - moved outside to prevent remounting
const WritingInterface = ({
  mode,
  currentEssay,
  localTitle,
  content,
  wordCount,
  isEditingTitle,
  showOrganizationPanel,
  colleges,
  summerPrograms,
  setMode,
  setIsEditingTitle,
  setShowOrganizationPanel,
  setContent,
  handleTitleUpdate,
  handleOrganizationUpdate,
  handleScratchpadUpdate,
  getWordCountColor,
  titleInputRef,
  contentTextareaRef,
  promptTextareaRef,
  onWeaveNavigation,
  isSaving,
  lastSaved,
  slotAttachedContent,
  showWritingSupport,
  setShowWritingSupport,
  // Essay slot system props
  selectedCollegeId,
  setSelectedCollegeId,
  availableSlots,
  loadingSlots,
  slotDrafts,
  loadSlotDrafts,
  essays,
  onSelectEssay,
  onCreateEssay,
  onUpdateEssay
}) => {
  // Calculate progress percentage
  const wordCountProgress = currentEssay?.word_limit
    ? Math.min((wordCount / currentEssay.word_limit) * 100, 100)
    : 0;

  const isOverLimit = currentEssay?.word_limit && wordCount > currentEssay.word_limit;
  const wordsOverLimit = isOverLimit ? wordCount - currentEssay.word_limit : 0;

  return (
    <div className="min-h-screen bg-stone-50">
      {/* College Notebook - show for supplemental essays with college_id */}
      {currentEssay?.type === 'supplemental' && currentEssay?.college_id && (
        <CollegeNotebook
          collegeId={currentEssay.college_id}
          essaySlotId={currentEssay.essay_slot_id}
        />
      )}

      {/* Header Bar */}
      <div className="bg-white border-b border-slate-900">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          {/* Left: Navigation */}
          <button
            onClick={() => setMode('selection')}
            className="flex items-center gap-2 text-slate-900 hover:text-slate-700 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Back to Essays</span>
          </button>

          {/* Right: Actions */}
          <div className="flex items-center gap-3">
            {/* Return to Builder button if essay has builder_data */}
            {currentEssay?.builder_data && (
              <button
                onClick={() => {
                  console.log('🚀 Return to Builder clicked! Current essay:', currentEssay);
                  console.log('🚀 Builder data:', currentEssay.builder_data);
                  setMode('builder');
                }}
                className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors text-sm font-medium"
              >
                <PenTool className="w-4 h-4 inline mr-2" />
                Return to Builder
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Essay Writing Area - Takes 2/3 of space */}
          <div className="lg:col-span-2">
            {/* Essay Header Card */}
            <div className="bg-white border-2 border-slate-900 rounded-xl p-8 mb-6 shadow-sm">
              <div className="flex items-start justify-between mb-2">
                {isEditingTitle ? (
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={localTitle || ''}
                    onChange={(e) => handleTitleUpdate(e.target.value)}
                    onBlur={() => setIsEditingTitle(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setIsEditingTitle(false);
                        contentTextareaRef.current?.focus();
                      }
                    }}
                    className="text-2xl font-bold text-slate-900 leading-tight max-w-3xl bg-transparent border-b-2 border-teal-400 outline-none w-full"
                    autoFocus
                  />
                ) : (
                  <h1
                    onClick={() => setIsEditingTitle(true)}
                    className="text-2xl font-bold text-slate-900 leading-tight max-w-3xl cursor-pointer hover:text-teal-600 transition-colors"
                  >
                    {localTitle || 'Untitled Essay'}
                  </h1>
                )}
              </div>

              {/* Metadata bar */}
              <div className="flex items-center gap-6 mt-4 text-xs text-slate-600">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-teal-500"></div>
                  <span>Draft in progress</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-3 h-3" />
                  <span>
                    {isSaving ? 'Saving...' : lastSaved ? `Auto-saved ${formatDistanceToNow(lastSaved, { addSuffix: true })}` : 'Not saved'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-900">{wordCount} words</span>
                  {currentEssay?.word_limit && (
                    <>
                      <span className="text-slate-400">/</span>
                      <span>{currentEssay.word_limit} target</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Essay Content - Immersive writing space */}
            <div className="bg-white border border-slate-300 rounded-xl p-12 shadow-sm min-h-[600px]">
              <textarea
                ref={contentTextareaRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Start writing your essay..."
                className="w-full h-full min-h-[500px] resize-none outline-none text-slate-900 leading-relaxed text-base"
                style={{
                  fontFamily: "'Reddit Sans', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                }}
                autoFocus={mode === 'writing' && !content}
              />
            </div>

            {/* Bottom bar with quick actions */}
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Coming next:</span>
                <span className="text-sm text-slate-900 font-medium">Review & Refinement</span>
              </div>
            </div>
          </div>

          {/* Contextual Assistance Panel - Right side 1/3 */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-4">
              {/* Writing Progress Card */}
              <div className="bg-white border border-slate-300 rounded-xl p-6">
                <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-500"></div>
                  Writing Progress
                </h3>

                {/* Word count progress */}
                {currentEssay?.word_limit && (
                  <div className="mb-4">
                    <div className="flex justify-between items-baseline mb-2">
                      <span className="text-xs text-slate-600">Word count</span>
                      <span className="text-sm font-medium text-slate-900">
                        {wordCount} / {currentEssay.word_limit}
                      </span>
                    </div>
                    <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          isOverLimit
                            ? 'bg-gradient-to-r from-coral-500 to-coral-600'
                            : 'bg-gradient-to-r from-teal-500 to-teal-600'
                        }`}
                        style={{ width: `${wordCountProgress}%` }}
                      ></div>
                    </div>
                    {isOverLimit && (
                      <p className="text-xs text-coral-600 mt-1">
                        {wordsOverLimit} words over target
                      </p>
                    )}
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex items-center gap-2 flex-col">
                  <button
                    onClick={() => setMode('selection')}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-stone-50 text-slate-900 border-2 border-slate-900 rounded-lg hover:bg-stone-100 transition-all text-sm font-medium shadow-sm"
                  >
                    <Save className="w-4 h-4" />
                    Save & Close
                  </button>

                  {/* Strategic Word Count Button */}
                  {(() => {
                    const targetWordCount = currentEssay?.word_limit || 650;
                    const currentWordCount = calculateWordCount(content);
                    const validation = validateChangeThreshold(currentWordCount, targetWordCount, 50); // 50-word threshold

                    if (validation.isValid && wordCount > 100) {
                      const isOverTarget = currentWordCount > targetWordCount;
                      return (
                        <button
                          onClick={() => setShowStrategicWordCount(true)}
                          className={`w-full flex items-center justify-center gap-2 px-4 py-2 border-2 rounded-lg transition-all text-sm font-medium shadow-sm ${
                            isOverTarget
                              ? 'bg-coral-50 text-coral-900 border-coral-500 hover:bg-coral-100'
                              : 'bg-teal-50 text-teal-900 border-teal-500 hover:bg-teal-100'
                          }`}
                        >
                          <Target className="w-4 h-4" />
                          {isOverTarget ? 'Strategic Cutting' : 'Strategic Development'}
                          <span className="font-bold">
                            ({isOverTarget ? '-' : '+'}{validation.changeNeeded})
                          </span>
                        </button>
                      );
                    }
                    return null;
                  })()}

                  {wordCount > 100 && (
                    <button
                      onClick={onWeaveNavigation}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 text-stone-50 border-2 border-slate-900 rounded-lg hover:bg-slate-800 transition-all text-sm font-medium shadow-sm"
                    >
                      <PenTool className="w-4 h-4" />
                      Review & Refine
                    </button>
                  )}

                  {/* Request Essay Review Button - Available at any stage with content */}
                  {wordCount > 0 && currentEssay?.id && (
                    <div className="pt-3 border-t border-slate-200 mt-3">
                      <ShareButton
                        itemType="essay"
                        itemId={currentEssay.id}
                        itemTitle={localTitle || currentEssay.title || 'Untitled Essay'}
                        currentStatus="draft"
                        variant="compact"
                        className="w-full justify-center"
                        onShareComplete={() => console.log('Essay shared for review')}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Essay Prompt - Always Visible */}
              <div className="bg-white border border-slate-300 rounded-xl p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                      <Edit3 className="w-4 h-4 text-slate-700" />
                      Essay Prompt
                    </h3>
                    {currentEssay?.essay_slot_id && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-medium rounded-full">
                        <CheckCircle className="w-3 h-3" />
                        Linked to Slot
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setShowOrganizationPanel(!showOrganizationPanel)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-stone-50 hover:bg-stone-100 border border-slate-300 rounded-lg transition-colors"
                  >
                    <FolderOpen className="w-3.5 h-3.5" />
                    {showOrganizationPanel ? 'Hide' : 'Details'}
                  </button>
                </div>
                <textarea
                  ref={promptTextareaRef}
                  value={currentEssay?.prompt || ''}
                  onChange={(e) => {
                    handleOrganizationUpdate('prompt', e.target.value);
                    // Auto-expand textarea
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  placeholder="Enter the essay prompt..."
                  className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none overflow-hidden"
                  style={{ minHeight: '80px' }}
                  rows="1"
                />
                {currentEssay?.word_limit && (
                  <p className="text-xs text-slate-600 mt-2">
                    Word limit: {currentEssay.word_limit}
                  </p>
                )}
              </div>

              {/* Autosave Scratchpad */}
              <div className="bg-stone-50 border border-slate-600 rounded-xl p-6">
                <h5 className="text-sm font-bold text-slate-900 mb-4">
                  Autosave Scratchpad
                </h5>
                <textarea
                  value={currentEssay?.scratchpad_notes || ''}
                  onChange={(e) => handleScratchpadUpdate(e.target.value)}
                  placeholder="Quick notes, ideas, reminders..."
                  className="w-full h-32 px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-700 placeholder:text-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                <p className="text-xs text-slate-500 mt-2">
                  Jot down ideas while writing - auto-saves with your essay
                </p>
              </div>

              {/* Organization Panel - Show as card when toggled */}
              {showOrganizationPanel && (
                <div className="bg-white border border-slate-300 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-slate-900">Organization</h3>
                    <button
                      onClick={() => setShowOrganizationPanel(false)}
                      className="p-1 hover:bg-stone-100 rounded transition-colors"
                    >
                      <X className="w-4 h-4 text-stone-500" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {/* Step 1: College Selection */}
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">
                        <Building className="w-3 h-3 inline mr-1" />
                        1. Select College
                      </label>
                      <select
                        value={selectedCollegeId || currentEssay?.college_id || ''}
                        onChange={(e) => {
                          const collegeId = e.target.value || null;
                          setSelectedCollegeId(collegeId);
                          // Also update essay's college_id immediately
                          if (collegeId) {
                            handleOrganizationUpdate('college_id', collegeId);
                          }
                        }}
                        className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      >
                        <option value="">Select a college...</option>
                        {colleges && colleges.length > 0 ? (
                          colleges.map(college => {
                            console.log('🔍 College in map:', college);
                            return (
                              <option key={college.id} value={college.id}>
                                {college.global_colleges?.name || college.name || 'Unnamed College'}
                              </option>
                            );
                          })
                        ) : (
                          <option disabled>No colleges added yet</option>
                        )}
                      </select>
                      {!colleges || colleges.length === 0 ? (
                        <p className="text-xs text-amber-600 mt-1">
                          Add colleges to your journey first
                        </p>
                      ) : null}
                    </div>

                    {/* Step 2: Essay Slot Selection */}
                    {selectedCollegeId && (
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">
                          <FileText className="w-3 h-3 inline mr-1" />
                          2. Select Essay Slot
                        </label>
                        {loadingSlots ? (
                          <div className="text-xs text-slate-500 py-2">Loading slots...</div>
                        ) : availableSlots.length > 0 ? (
                          <select
                            value={currentEssay?.essay_slot_id || ''}
                            onChange={(e) => {
                              const slotId = e.target.value || null;
                              if (slotId) {
                                const slot = availableSlots.find(s => s.id === slotId);
                                if (slot) {
                                  // Auto-populate prompt, word limit, and type from slot
                                  handleOrganizationUpdate('essay_slot_id', slotId);
                                  handleOrganizationUpdate('prompt', slot.prompt_text || '');
                                  handleOrganizationUpdate('word_limit', slot.word_limit || null);
                                  handleOrganizationUpdate('type', slot.slot_type === 'personal_statement' ? 'personal' : 'supplemental');
                                }
                              }
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                          >
                            <option value="">Select an essay slot...</option>
                            {availableSlots.map(slot => (
                              <option key={slot.id} value={slot.id}>
                                {slot.slot_type === 'personal_statement' ? 'Personal Statement' : `Essay ${slot.essay_slot}`}
                                {slot.prompt_text ? ` - ${slot.prompt_text.substring(0, 50)}...` : ''}
                                {slot.word_limit ? ` (${slot.word_limit} words)` : ''}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <p className="text-xs text-amber-600 mt-1">
                            No essay slots found for this college. Slots are auto-generated when colleges are added.
                          </p>
                        )}
                      </div>
                    )}

                    {/* Other Drafts for This Slot */}
                    {currentEssay?.essay_slot_id && slotDrafts.length > 1 && (
                      <div className="pt-4 border-t border-stone-200">
                        <label className="block text-xs font-medium text-slate-700 mb-2">
                          <FileText className="w-3 h-3 inline mr-1" />
                          Other Drafts for This Slot
                        </label>
                        <div className="space-y-2">
                          {slotDrafts.map(draft => (
                            <div
                              key={draft.id}
                              className={`flex items-center justify-between p-2 rounded-lg border ${
                                draft.id === currentEssay.id
                                  ? 'border-teal-500 bg-teal-50'
                                  : 'border-stone-200 bg-white hover:border-stone-300'
                              }`}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-xs font-medium text-slate-900 truncate">
                                    {draft.title || 'Untitled Draft'}
                                  </p>
                                  {draft.is_primary && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 bg-teal-100 text-teal-700 text-xs font-medium rounded">
                                      Primary
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-slate-500">
                                  {draft.word_count || 0} words • {new Date(draft.updated_at).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                {!draft.is_primary && draft.id === currentEssay.id && (
                                  <button
                                    onClick={async () => {
                                      await onUpdateEssay(draft.id, { is_primary: true });
                                      // Reload drafts to show updated primary status
                                      loadSlotDrafts(currentEssay.essay_slot_id);
                                    }}
                                    className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                                  >
                                    Set as Primary
                                  </button>
                                )}
                                {draft.id !== currentEssay.id && (
                                  <button
                                    onClick={() => onSelectEssay(essays.find(e => e.id === draft.id))}
                                    className="text-xs text-teal-600 hover:text-teal-700 font-medium"
                                  >
                                    Switch
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        <button
                          onClick={async () => {
                            // Create a new draft for the same slot
                            const newDraft = {
                              title: `${currentEssay.title || 'Essay'} - Draft ${slotDrafts.length + 1}`,
                              type: currentEssay.type,
                              college_id: currentEssay.college_id,
                              essay_slot_id: currentEssay.essay_slot_id,
                              prompt: currentEssay.prompt,
                              word_limit: currentEssay.word_limit,
                              due_date: currentEssay.due_date,
                              content: '',
                              status: 'draft',
                              word_count: 0,
                              is_primary: false
                            };
                            const created = await onCreateEssay(newDraft);
                            if (created) {
                              onSelectEssay(created);
                            }
                          }}
                          className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors"
                        >
                          <FileText className="w-3.5 h-3.5" />
                          Create Alternate Draft
                        </button>
                      </div>
                    )}

                    {/* Optional: Due Date */}
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        Due Date (Optional)
                      </label>
                      <input
                        type="date"
                        value={currentEssay?.due_date ? currentEssay.due_date.split('T')[0] : ''}
                        onChange={(e) => handleOrganizationUpdate('due_date', e.target.value || null)}
                        className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Overrides the college's deadline for this specific essay
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Essay Builder template selection component - moved outside to prevent remounting
const EssayBuilderSelection = ({ launchBuilder, setSelectedOption }) => (
  <div className="p-8">
    <div className="flex items-center justify-between mb-6">
      <h2 className="text-2xl font-bold text-slate-900">Choose Your Essay Story</h2>
      <button
        onClick={() => setSelectedOption(null)}
        className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
      >
        <X className="w-5 h-5 text-slate-600" />
      </button>
    </div>
    <p className="text-slate-600 mb-8 text-center max-w-2xl mx-auto">
      Our Essay Builder guides you through writing with targeted prompts and coaching. Choose the template that best matches your story.
    </p>
    
    {/* General Templates Section */}
    <div className="mb-12">
      <h3 className="text-lg font-semibold text-slate-800 mb-6 text-center">General Templates</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {/* Personal Growth */}
        <button
          onClick={() => launchBuilder('personalGrowth')}
          className="bg-gradient-to-br from-violet-50 to-violet-100 rounded-xl p-6 shadow-sm border border-violet-200 hover:from-violet-100 hover:to-violet-200 hover:shadow-md transition-all group text-left"
        >
          <div className="flex flex-col items-center space-y-4">
            <div className="w-12 h-12 bg-violet-200 rounded-lg flex items-center justify-center group-hover:bg-violet-300 transition-colors">
              <MessageSquare className="w-6 h-6 text-violet-700" />
            </div>
            <div className="text-center">
              <h4 className="text-lg font-semibold text-slate-900 mb-2">Personal Growth</h4>
              <p className="text-sm text-slate-600 mb-3">Perfect for stories about overcoming personal limitations, developing new skills, changing perspectives, or building confidence</p>
              <div className="flex flex-wrap gap-1 justify-center">
                <span className="px-2 py-1 bg-violet-100 text-violet-700 text-xs rounded-full">Character development</span>
                <span className="px-2 py-1 bg-violet-100 text-violet-700 text-xs rounded-full">Self-improvement</span>
              </div>
            </div>
          </div>
        </button>
        
        {/* Overcoming Challenge */}
        <button
          onClick={() => launchBuilder('overcomingChallenge')}
          className="bg-gradient-to-br from-azure-50 to-azure-100 rounded-xl p-6 shadow-sm border border-azure-200 hover:from-azure-100 hover:to-azure-200 hover:shadow-md transition-all group text-left"
        >
          <div className="flex flex-col items-center space-y-4">
            <div className="w-12 h-12 bg-azure-200 rounded-lg flex items-center justify-center group-hover:bg-azure-300 transition-colors">
              <AlertCircle className="w-6 h-6 text-azure-700" />
            </div>
            <div className="text-center">
              <h4 className="text-lg font-semibold text-slate-900 mb-2">Challenge Story</h4>
              <p className="text-sm text-slate-600 mb-3">Ideal for demonstrating problem-solving, resilience, and personal strength when facing external obstacles</p>
              <div className="flex flex-wrap gap-1 justify-center">
                <span className="px-2 py-1 bg-azure-100 text-azure-700 text-xs rounded-full">Resilience</span>
                <span className="px-2 py-1 bg-azure-100 text-azure-700 text-xs rounded-full">Problem-solving</span>
              </div>
            </div>
          </div>
        </button>
        
        {/* Passion Project */}
        <button
          onClick={() => launchBuilder('passionProject')}
          className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-6 shadow-sm border border-emerald-200 hover:from-emerald-100 hover:to-emerald-200 hover:shadow-md transition-all group text-left"
        >
          <div className="flex flex-col items-center space-y-4">
            <div className="w-12 h-12 bg-emerald-200 rounded-lg flex items-center justify-center group-hover:bg-emerald-300 transition-colors">
              <Sparkles className="w-6 h-6 text-emerald-700" />
            </div>
            <div className="text-center">
              <h4 className="text-lg font-semibold text-slate-900 mb-2">Passion Project</h4>
              <p className="text-sm text-slate-600 mb-3">Great for showcasing intellectual curiosity, sustained commitment, creative pursuits, or community initiatives</p>
              <div className="flex flex-wrap gap-1 justify-center">
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">Deep interest</span>
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">Innovation</span>
              </div>
            </div>
          </div>
        </button>
      </div>
    </div>

    {/* Common App Templates Section */}
    <div className="mb-8">
      <div className="flex items-center justify-center gap-3 mb-6">
        <h3 className="text-lg font-semibold text-slate-800">Common App Prompts</h3>
        <span className="bg-gradient-to-r from-indigo-500 to-azure-600 text-white px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide">Common App</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
        {/* Essential Identity */}
        <button
          onClick={() => launchBuilder('essentialIdentity')}
          className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-5 shadow-sm border-l-4 border-l-indigo-500 border-t border-r border-b border-indigo-200 hover:from-indigo-100 hover:to-indigo-200 hover:shadow-md transition-all group text-left"
        >
          <div className="flex flex-col space-y-3">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 bg-indigo-200 rounded-lg flex items-center justify-center group-hover:bg-indigo-300 transition-colors flex-shrink-0">
                <Target className="w-5 h-5 text-indigo-700" />
              </div>
              <span className="text-xs text-indigo-600 font-medium">#1</span>
            </div>
            <div>
              <h4 className="text-base font-semibold text-slate-900 mb-2">Essential Identity</h4>
              <p className="text-sm text-slate-600 mb-2">For backgrounds, identities, interests, or talents so meaningful your application would be incomplete without them</p>
              <div className="flex flex-wrap gap-1">
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">Identity</span>
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">Background</span>
              </div>
            </div>
          </div>
        </button>
        
        {/* Learning from Failure */}
        <button
          onClick={() => launchBuilder('learningFromFailure')}
          className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-5 shadow-sm border-l-4 border-l-indigo-500 border-t border-r border-b border-indigo-200 hover:from-indigo-100 hover:to-indigo-200 hover:shadow-md transition-all group text-left"
        >
          <div className="flex flex-col space-y-3">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 bg-indigo-200 rounded-lg flex items-center justify-center group-hover:bg-indigo-300 transition-colors flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-indigo-700" />
              </div>
              <span className="text-xs text-indigo-600 font-medium">#2</span>
            </div>
            <div>
              <h4 className="text-base font-semibold text-slate-900 mb-2">Learning from Failure</h4>
              <p className="text-sm text-slate-600 mb-2">For recounting challenges, setbacks, or failures that taught fundamental lessons leading to later success</p>
              <div className="flex flex-wrap gap-1">
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">Growth</span>
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">Learning</span>
              </div>
            </div>
          </div>
        </button>
        
        {/* Challenging Assumptions */}
        <button
          onClick={() => launchBuilder('challengingAssumptions')}
          className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-5 shadow-sm border-l-4 border-l-indigo-500 border-t border-r border-b border-indigo-200 hover:from-indigo-100 hover:to-indigo-200 hover:shadow-md transition-all group text-left"
        >
          <div className="flex flex-col space-y-3">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 bg-indigo-200 rounded-lg flex items-center justify-center group-hover:bg-indigo-300 transition-colors flex-shrink-0">
                <Brain className="w-5 h-5 text-indigo-700" />
              </div>
              <span className="text-xs text-indigo-600 font-medium">#3</span>
            </div>
            <div>
              <h4 className="text-base font-semibold text-slate-900 mb-2">Challenging Assumptions</h4>
              <p className="text-sm text-slate-600 mb-2">For times when you questioned or challenged a belief or idea, showing intellectual courage and independent thinking</p>
              <div className="flex flex-wrap gap-1">
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">Critical thinking</span>
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">Courage</span>
              </div>
            </div>
          </div>
        </button>
        
        {/* Unexpected Gratitude */}
        <button
          onClick={() => launchBuilder('unexpectedGratitude')}
          className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-5 shadow-sm border-l-4 border-l-indigo-500 border-t border-r border-b border-indigo-200 hover:from-indigo-100 hover:to-indigo-200 hover:shadow-md transition-all group text-left"
        >
          <div className="flex flex-col space-y-3">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 bg-indigo-200 rounded-lg flex items-center justify-center group-hover:bg-indigo-300 transition-colors flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-indigo-700" />
              </div>
              <span className="text-xs text-indigo-600 font-medium">#4</span>
            </div>
            <div>
              <h4 className="text-base font-semibold text-slate-900 mb-2">Unexpected Gratitude</h4>
              <p className="text-sm text-slate-600 mb-2">For reflecting on something someone did that made you happy or thankful in a surprising way</p>
              <div className="flex flex-wrap gap-1">
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">Gratitude</span>
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">Relationships</span>
              </div>
            </div>
          </div>
        </button>
        
        {/* Defining Moment */}
        <button
          onClick={() => launchBuilder('definingMoment')}
          className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-5 shadow-sm border-l-4 border-l-indigo-500 border-t border-r border-b border-indigo-200 hover:from-indigo-100 hover:to-indigo-200 hover:shadow-md transition-all group text-left"
        >
          <div className="flex flex-col space-y-3">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 bg-indigo-200 rounded-lg flex items-center justify-center group-hover:bg-indigo-300 transition-colors flex-shrink-0">
                <Zap className="w-5 h-5 text-indigo-700" />
              </div>
              <span className="text-xs text-indigo-600 font-medium">#5</span>
            </div>
            <div>
              <h4 className="text-base font-semibold text-slate-900 mb-2">Defining Moment</h4>
              <p className="text-sm text-slate-600 mb-2">For accomplishments, events, or realizations that sparked personal growth and new understanding of yourself or others</p>
              <div className="flex flex-wrap gap-1">
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">Transformation</span>
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">Insight</span>
              </div>
            </div>
          </div>
        </button>
        
        {/* Intellectual Passion */}
        <button
          onClick={() => launchBuilder('intellectualPassion')}
          className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-5 shadow-sm border-l-4 border-l-indigo-500 border-t border-r border-b border-indigo-200 hover:from-indigo-100 hover:to-indigo-200 hover:shadow-md transition-all group text-left"
        >
          <div className="flex flex-col space-y-3">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 bg-indigo-200 rounded-lg flex items-center justify-center group-hover:bg-indigo-300 transition-colors flex-shrink-0">
                <Lightbulb className="w-5 h-5 text-indigo-700" />
              </div>
              <span className="text-xs text-indigo-600 font-medium">#6</span>
            </div>
            <div>
              <h4 className="text-base font-semibold text-slate-900 mb-2">Intellectual Passion</h4>
              <p className="text-sm text-slate-600 mb-2">For topics, ideas, or concepts so engaging that they make you lose track of time - showcasing deep intellectual curiosity</p>
              <div className="flex flex-wrap gap-1">
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">Curiosity</span>
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">Academic passion</span>
              </div>
            </div>
          </div>
        </button>
      </div>
    </div>
    
    <div className="text-center">
      <p className="text-sm text-stone-500">
        Each template includes targeted prompts, coaching feedback, and real-time essay building
      </p>
    </div>
  </div>
);

// Ideas generation component - redirects to Ideas hub - moved outside to prevent remounting
const IdeasGeneration = ({ selectedOption, onModeChange }) => {
  // When Ideas is selected, we should navigate to the Ideas tab in the sidebar
  // This is handled by the parent component (EssayMain)
  useEffect(() => {
    if (selectedOption === 'ideas' && onModeChange) {
      // Notify parent to switch to Ideas tab
      onModeChange('ideas');
    }
  }, [selectedOption, onModeChange]);
  
  return (
    <div className="p-8">
      <div className="flex flex-col items-center justify-center py-12">
        <Brain className="w-16 h-16 text-amber-500 mb-4" />
        <p className="text-slate-600">Redirecting to Ideas hub...</p>
      </div>
    </div>
  );
};

// Existing drafts component - moved outside to prevent remounting
const ExistingDrafts = ({ essays, setMode, setCurrentEssay, setContent, setWordCount, onSelectEssay, setSelectedOption, onDeleteEssay }) => {
  const [deletingId, setDeletingId] = useState(null);
  
  // Helper function to get essay excerpt
  const getExcerpt = (content, maxLength = 150) => {
    if (!content) return 'No content yet...';
    const text = content.trim();
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    try {
      const date = parseISO(dateString);
      return format(date, 'MMM d, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  // Helper function to get relative time
  const getRelativeTime = (dateString) => {
    if (!dateString) return '';
    try {
      const date = parseISO(dateString);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return '';
    }
  };

  // Check if essay was recently modified (within last 24 hours)
  const isRecentlyModified = (dateString) => {
    if (!dateString) return false;
    try {
      const date = parseISO(dateString);
      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);
      return diffInHours <= 24;
    } catch {
      return false;
    }
  };
  
  // Handle delete with confirmation
  const handleDelete = async (essayId) => {
    if (deletingId === essayId) {
      // Second click confirms deletion
      await onDeleteEssay(essayId);
      setDeletingId(null);
    } else {
      // First click shows confirmation state
      setDeletingId(essayId);
      // Reset confirmation after 3 seconds
      setTimeout(() => setDeletingId(null), 3000);
    }
  };

  return (
    <div className="p-8 h-full flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Your Drafts</h2>
          <p className="text-sm text-stone-500 mt-1">
            {essays.filter(e => !e.status || e.status === 'draft').length} {essays.filter(e => !e.status || e.status === 'draft').length === 1 ? 'draft' : 'drafts'} available
          </p>
        </div>
        <button
          onClick={() => setSelectedOption(null)}
          className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
        >
          <X className="w-5 h-5 text-slate-600" />
        </button>
      </div>
      <div className="space-y-4 overflow-y-auto flex-1 pr-2">
        {essays.filter(e => !e.status || e.status === 'draft').map(essay => (
          <div
            key={essay.id}
            className="relative w-full text-left p-5 bg-white rounded-lg border border-stone-200 hover:border-violet-300 hover:shadow-md transition-all group"
          >
            <button
              onClick={() => {
                onSelectEssay(essay);
                // Smart routing: If essay has analysis, go to refinement mode, otherwise writing mode
                if (essay.has_analysis) {
                  setMode('refinement');
                } else {
                  setMode('writing');
                }
              }}
              className="w-full text-left"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  {/* Title and type */}
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-slate-900 text-lg truncate flex-1" title={essay.title || 'Untitled Essay'}>
                      {essay.title || 'Untitled Essay'}
                    </h3>
                    {essay.has_analysis && (
                      <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap flex items-center gap-1 ${
                        essay.analysis_word_count && Math.abs(essay.word_count - essay.analysis_word_count) > essay.word_count * 0.2
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {essay.analysis_word_count && Math.abs(essay.word_count - essay.analysis_word_count) > essay.word_count * 0.2 ? (
                          <>
                            <AlertCircle className="w-3 h-3" />
                            Needs Re-analysis
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-3 h-3" />
                            Analyzed
                          </>
                        )}
                      </span>
                    )}
                    <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-xs rounded-full whitespace-nowrap">
                      {essay.type || 'personal'}
                    </span>
                  </div>
                  
                  {/* College/Program association if exists */}
                  {(essay.college_name || essay.program_name) && (
                    <div className="flex items-center gap-1 mb-2">
                      <Building className="w-3 h-3 text-stone-400" />
                      <span className="text-xs text-stone-500">
                        {essay.college_name || essay.program_name}
                      </span>
                    </div>
                  )}
                  
                  {/* Excerpt */}
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                    {getExcerpt(essay.content)}
                  </p>
                  
                  {/* Analysis status detail */}
                  {essay.has_analysis && essay.last_analyzed_at && (
                    <p className="text-xs text-stone-500 mb-2">
                      Last analyzed {formatDistanceToNow(parseISO(essay.last_analyzed_at), { addSuffix: true })}
                    </p>
                  )}
                  
                  {/* Meta information */}
                  <div className="flex items-center gap-4 text-xs text-stone-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      <span>Started: {formatDate(essay.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span className={isRecentlyModified(essay.updated_at) ? 'text-emerald-600 font-medium' : ''}>
                        Modified: {getRelativeTime(essay.updated_at)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      <span>{essay.word_count || 0} words</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2 ml-4">
                  <ChevronRight className="w-5 h-5 text-stone-400 group-hover:text-violet-500 transition-colors flex-shrink-0 mt-1" />
                </div>
              </div>
            </button>
            
            {/* Action buttons */}
            <div className="absolute top-4 right-4 flex items-center gap-2">
              {/* Share button */}
              <div onClick={(e) => e.stopPropagation()}>
                <ShareButton
                  itemType="essay"
                  itemId={essay.id}
                  itemTitle={essay.title || 'Untitled Essay'}
                  currentStatus={essay.status || 'draft'}
                  variant="icon-only"
                />
              </div>

              {/* Delete button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(essay.id);
                }}
                className={`p-2 rounded-lg transition-all ${
                  deletingId === essay.id
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'text-stone-400 hover:text-red-500 hover:bg-red-50'
                }`}
                title={deletingId === essay.id ? 'Click again to confirm delete' : 'Delete draft'}
              >
                <Trash2 className="w-4 h-4" />
                {deletingId === essay.id && (
                  <span className="absolute -bottom-6 right-0 text-xs text-red-500 whitespace-nowrap">
                    Click again to confirm
                  </span>
                )}
              </button>
            </div>
          </div>
        ))}
        {essays.filter(e => !e.status || e.status === 'draft').length === 0 && (
          <div className="text-center text-stone-500 py-8">
            No drafts yet. Start writing!
          </div>
        )}
      </div>
    </div>
  );
};

const EssayDevelopmentInterface = ({
  userId,
  essays = [],
  selectedEssay,
  slotAttachedContent = {},
  onSelectEssay,
  onCreateEssay,
  onUpdateEssay,
  onDeleteEssay,
  colleges = [],
  summerPrograms = [],
  initialOption = null,
  initialMode = null,
  onModeChange,
  onBuilderControlsChange
}) => {
  const navigate = useNavigate();
  
  // States for interface modes - respect initialMode if provided
  const [mode, setMode] = useState(initialMode || 'selection');
  const [selectedOption, setSelectedOption] = useState(initialOption);
  
  const [builderTemplate, setBuilderTemplate] = useState(null);
  
  // Notify parent component of mode changes
  const handleModeChange = useCallback((newMode) => {
    onModeChange?.(newMode);
  }, [onModeChange]);

  useEffect(() => {
    handleModeChange(mode);
  }, [mode, handleModeChange]);

  // Handle builder controls change
  const handleBuilderControlsChange = useCallback((controls) => {
    onBuilderControlsChange?.(controls);
  }, [onBuilderControlsChange]);
  
  // States for writing interface
  const [currentEssay, setCurrentEssay] = useState(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [showOrganizationPanel, setShowOrganizationPanel] = useState(false);
  const [showWritingSupport, setShowWritingSupport] = useState(false);
  const [showStrategicWordCount, setShowStrategicWordCount] = useState(false);
  const [content, setContent] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [localTitle, setLocalTitle] = useState(''); // Local state for immediate title updates

  // States for essay slot selection
  const [selectedCollegeId, setSelectedCollegeId] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotDrafts, setSlotDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  
  // Refs
  const titleInputRef = useRef(null);
  const contentTextareaRef = useRef(null);
  const promptTextareaRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const titleSaveTimeoutRef = useRef(null); // Separate timeout for title debouncing

  // Initialize with selection screen
  useEffect(() => {
    if (selectedEssay) {
      setCurrentEssay(selectedEssay);
      setContent(selectedEssay.content || '');
      setWordCount(selectedEssay.word_count || 0);
      setLocalTitle(selectedEssay.title || ''); // Sync local title
      // Initialize selectedCollegeId from essay if it has one
      if (selectedEssay.college_id) {
        setSelectedCollegeId(selectedEssay.college_id);
      }
      // Only switch to writing mode if we're not in builder or refinement mode
      // This prevents the builder/refinement from being interrupted
      if (mode !== 'builder' && mode !== 'refinement') {
        setMode('writing');
      }
    }
  }, [selectedEssay, mode]);

  // Auto-save functionality with auth error handling
  const autoSave = useCallback(async () => {
    if (currentEssay && currentEssay.id && content !== currentEssay.content) {
      setIsSaving(true);
      
      try {
        // First check if we have a valid session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError || !session) {
          console.log('Session expired, attempting to refresh...');
          // Try to refresh the session
          const { data: { session: newSession }, error: refreshError } = await supabase.auth.refreshSession();
          
          if (refreshError || !newSession) {
            // Session refresh failed, pause auto-save
            console.error('Session refresh failed, pausing auto-save');
            toast.error('Your session has expired. Please save your work and refresh the page.', {
              duration: 5000,
              icon: '⚠️'
            });
            setIsSaving(false);
            return;
          }
        }
        
        // Proceed with the update
        await onUpdateEssay(currentEssay.id, {
          content,
          word_count: wordCount,
          updated_at: new Date().toISOString()
        });
        
        setLastSaved(new Date());
      } catch (error) {
        console.error('Auto-save error:', error);
        
        // Check if it's an auth error
        if (error.message?.includes('401') || error.message?.includes('JWT') || error.message?.includes('token')) {
          toast.error('Session expired. Your work is saved locally. Please refresh the page to continue.', {
            duration: 5000,
            icon: '🔐'
          });
        } else {
          // For other errors, show a brief notification
          toast.error('Auto-save failed. Your work is preserved locally.', {
            duration: 3000
          });
        }
      } finally {
        setIsSaving(false);
      }
    }
  }, [currentEssay, content, wordCount, onUpdateEssay]);

  // Weave navigation function
  const handleWeaveNavigation = useCallback(() => {
    if (!currentEssay) {
      toast.error('No essay to refine');
      return;
    }
    
    // Trigger save before navigating (autosave will handle it)
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    autoSave(); // Save immediately before navigation
    
    // Navigate to refinement with essay data
    navigate('/college/essays/refinement', {
      state: {
        essay: {
          id: currentEssay.id,
          title: localTitle || currentEssay.title || 'Essay Draft',
          content: content,
          word_count: wordCount,
          word_limit: currentEssay.word_limit,
          college_id: currentEssay.college_id,
          summer_program_id: currentEssay.summer_program_id,
          builder_data: currentEssay.builder_data,
          status: currentEssay.status
        }
      }
    });
  }, [currentEssay, localTitle, content, wordCount, autoSave, navigate]);

  // Debounced auto-save
  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      autoSave();
    }, 2000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [content, autoSave]);

  // Word count calculation
  useEffect(() => {
    const words = content.trim().split(/\s+/).filter(word => word.length > 0);
    setWordCount(words.length);
  }, [content]);

  // Auto-expand prompt textarea when prompt changes
  useEffect(() => {
    if (promptTextareaRef.current) {
      promptTextareaRef.current.style.height = 'auto';
      promptTextareaRef.current.style.height = promptTextareaRef.current.scrollHeight + 'px';
    }
  }, [currentEssay?.prompt]);

  // Load essay slots when college is selected
  const loadEssaySlotsForCollege = useCallback(async (collegeId) => {
    if (!collegeId || !userId) {
      setAvailableSlots([]);
      return;
    }

    setLoadingSlots(true);
    try {
      const { data, error } = await supabase
        .from('essay_slots')
        .select('*')
        .eq('user_id', userId)
        .eq('college_id', collegeId)
        .order('essay_slot', { ascending: true });

      if (error) throw error;
      console.log('📝 Loaded essay slots for college:', data);
      setAvailableSlots(data || []);
    } catch (error) {
      console.error('Error loading essay slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, [userId]);

  // Load slots when college ID changes
  useEffect(() => {
    if (selectedCollegeId) {
      loadEssaySlotsForCollege(selectedCollegeId);
    } else {
      setAvailableSlots([]);
    }
  }, [selectedCollegeId, loadEssaySlotsForCollege]);

  // Load other drafts for the current essay's slot
  const loadSlotDrafts = useCallback(async (slotId) => {
    if (!slotId || !userId) {
      setSlotDrafts([]);
      return;
    }

    setLoadingDrafts(true);
    try {
      const { data, error } = await supabase
        .from('college_journey_essays')
        .select('id, title, is_primary, updated_at, word_count, status')
        .eq('user_id', userId)
        .eq('essay_slot_id', slotId)
        .eq('deleted', false)
        .order('is_primary', { ascending: false })
        .order('updated_at', { ascending: false });

      if (error) throw error;
      console.log('📝 Loaded drafts for slot:', data);
      setSlotDrafts(data || []);
    } catch (error) {
      console.error('Error loading slot drafts:', error);
      setSlotDrafts([]);
    } finally {
      setLoadingDrafts(false);
    }
  }, [userId]);

  // Load drafts when essay's slot changes
  useEffect(() => {
    if (currentEssay?.essay_slot_id) {
      loadSlotDrafts(currentEssay.essay_slot_id);
    } else {
      setSlotDrafts([]);
    }
  }, [currentEssay?.essay_slot_id, loadSlotDrafts]);

  // Auto-focus textarea when entering writing mode with a new essay
  useEffect(() => {
    if (mode === 'writing' && currentEssay && !content && contentTextareaRef.current) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        contentTextareaRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [mode, currentEssay?.id, content]); // Only depend on ID to prevent focus on title changes

  // Handle creating new essay
  const handleNewEssay = async () => {
    const newEssay = {
      title: 'Untitled Essay',
      type: 'personal',
      college_id: null,
      summer_program_id: null,
      prompt: '',
      word_limit: null,
      due_date: null,
      content: '',
      status: 'draft',
      word_count: 0
    };
    
    try {
      const created = await onCreateEssay(newEssay);
      if (created && created.id) {
        setCurrentEssay(created);
        setLocalTitle(created.title || 'Untitled Essay'); // Set local title for new essay
        setContent('');
        setWordCount(0);
        setMode('writing');
        setIsEditingTitle(false);
      }
    } catch (error) {
      console.error('Error creating new essay:', error);
    }
  };

  // Handle title update with debouncing
  const handleTitleUpdate = useCallback((newTitle) => {
    // Update local state immediately for responsive UI
    setLocalTitle(newTitle);
    
    // Update currentEssay locally to keep it in sync
    if (currentEssay) {
      setCurrentEssay(prev => ({
        ...prev,
        title: newTitle
      }));
    }
    
    // Clear existing timeout
    if (titleSaveTimeoutRef.current) {
      clearTimeout(titleSaveTimeoutRef.current);
    }
    
    // Debounce the database save
    titleSaveTimeoutRef.current = setTimeout(() => {
      if (currentEssay && currentEssay.id && newTitle !== currentEssay.title) {
        onUpdateEssay(currentEssay.id, {
          title: newTitle
        });
      }
    }, 800); // 800ms delay before saving to database
  }, [currentEssay, onUpdateEssay]);

  // Handle organization panel updates
  const handleOrganizationUpdate = useCallback((field, value) => {
    if (currentEssay && currentEssay.id) {
      const updated = {
        ...currentEssay,
        [field]: value
      };
      setCurrentEssay(updated);
      onUpdateEssay(currentEssay.id, {
        [field]: value
      });
    }
  }, [currentEssay, onUpdateEssay]);

  // Refs for scratchpad debouncing
  const scratchpadTimeoutRef = useRef(null);

  // Handle scratchpad updates with debouncing
  const handleScratchpadUpdate = useCallback((value) => {
    // Update local state immediately
    if (currentEssay) {
      setCurrentEssay(prev => ({
        ...prev,
        scratchpad_notes: value
      }));
    }

    // Clear existing timeout
    if (scratchpadTimeoutRef.current) {
      clearTimeout(scratchpadTimeoutRef.current);
    }

    // Debounce the database save
    scratchpadTimeoutRef.current = setTimeout(() => {
      if (currentEssay && currentEssay.id) {
        onUpdateEssay(currentEssay.id, {
          scratchpad_notes: value
        });
      }
    }, 1000); // 1 second delay before saving to database
  }, [currentEssay, onUpdateEssay]);

  // Get word count color based on limit
  const getWordCountColor = useCallback(() => {
    if (!currentEssay?.word_limit) return 'text-stone-500';
    
    const ratio = wordCount / currentEssay.word_limit;
    if (ratio > 1) return 'text-red-500 font-semibold';
    if (ratio > 0.9) return 'text-yellow-500';
    return 'text-stone-500';
  }, [currentEssay, wordCount]);

  // Handle Conversational Builder launch
  const launchBuilder = (template) => {
    setBuilderTemplate(template);
    setMode('builder');
  };

  // Handle save from builder
  const handleBuilderSave = async (essayData) => {
    // Handle both old and new data formats
    let content, wordCount;
    
    if (typeof essayData === 'string') {
      // Old format - just a string content
      content = essayData;
      wordCount = essayData.trim() ? essayData.trim().split(/\s+/).length : 0;
    } else if (essayData.content) {
      // New format with content field
      content = essayData.content;
      wordCount = essayData.wordCount || 0;
    } else if (essayData.sections && typeof essayData.sections === 'object') {
      // New format with sections object
      content = Object.values(essayData.sections).filter(text => text).join('\n\n');
      wordCount = essayData.wordCount || content.trim().split(/\s+/).length;
    } else {
      console.error('Invalid essay data format:', essayData);
      return;
    }
    
    // Convert builder data to essay format
    const newEssay = {
      title: essayData.title || currentEssay?.title || 'Essay Draft',
      type: 'personal',
      content: content,
      word_count: wordCount,
      status: 'draft',
      builder_data: essayData // Store the structured data
    };
    
    if (currentEssay?.id) {
      // Update existing essay without changing currentEssay reference
      await onUpdateEssay(currentEssay.id, newEssay);
      // Update currentEssay with the same ID to preserve component state
      setCurrentEssay(prev => ({ ...prev, ...newEssay }));
    } else {
      const created = await onCreateEssay(newEssay);
      if (created) {
        // Only set currentEssay once when first creating
        setCurrentEssay(created);
      }
    }
  };

  // Render method - removed debug console.log to prevent unnecessary re-renders
  return (
    <div className="h-full min-h-full bg-white rounded-xl shadow-sm overflow-auto">
      {mode === 'builder' ? (
        <ConversationalEssayBuilder
          selectedEssay={currentEssay}
          slotAttachedContent={slotAttachedContent}
          onSaveEssay={handleBuilderSave}
          onExportEssay={(data) => {
            // Handle export
            console.log('Exporting essay:', data);
          }}
          onBack={() => {
            setMode('selection');
            setBuilderTemplate(null);
          }}
          onControlsChange={handleBuilderControlsChange}
          initialTemplate={builderTemplate}
        />
      ) : mode === 'refinement' ? (
        <EssayRefinementInterface
          initialEssayContent={content}
          essayTitle={currentEssay?.title}
          essayId={currentEssay?.id}
          preSelectedEssay={currentEssay}
          slotAttachedContent={slotAttachedContent}
          onNavigateBack={() => setMode('selection')}
          onSwitchToDraft={() => setMode('writing')}
        />
      ) : mode === 'selection' ? (
        selectedOption === 'builder' ? (
          <EssayBuilderSelection
            launchBuilder={launchBuilder}
            setSelectedOption={setSelectedOption}
          />
        ) : selectedOption === 'ideas' ? (
          <IdeasGeneration
            selectedOption={selectedOption}
            onModeChange={onModeChange}
          />
        ) : selectedOption === 'existing' ? (
          <div className="min-h-screen bg-stone-50 p-8">
            <div className="max-w-[1400px] mx-auto">
              {/* Back button */}
              <button
                onClick={() => setSelectedOption(null)}
                className="mb-6 flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Back to Start
              </button>

              <DraftLibrary
                essays={essays}
                onSelectEssay={(essay) => {
                  onSelectEssay(essay);
                  setCurrentEssay(essay);
                  setContent(essay.content || '');
                  setWordCount(essay.word_count || 0);
                  setMode('writing');
                }}
                onCreateNew={handleNewEssay}
                onDeleteEssay={onDeleteEssay}
                colleges={colleges}
              />
            </div>
          </div>
        ) : (
          <SelectionScreen
            handleNewEssay={handleNewEssay}
            setSelectedOption={setSelectedOption}
            recentWork={essays.filter(essay => essay.status && essay.status !== 'draft').slice(0, 3)}
            draftCount={essays.filter(e => !e.status || e.status === 'draft').length}
          />
        )
      ) : mode === 'writing' ? (
        <WritingInterface
          mode={mode}
          currentEssay={currentEssay}
          localTitle={localTitle}
          content={content}
          wordCount={wordCount}
          isEditingTitle={isEditingTitle}
          showOrganizationPanel={showOrganizationPanel}
          colleges={colleges}
          summerPrograms={summerPrograms}
          setMode={setMode}
          setIsEditingTitle={setIsEditingTitle}
          setShowOrganizationPanel={setShowOrganizationPanel}
          setContent={setContent}
          handleTitleUpdate={handleTitleUpdate}
          handleOrganizationUpdate={handleOrganizationUpdate}
          handleScratchpadUpdate={handleScratchpadUpdate}
          getWordCountColor={getWordCountColor}
          titleInputRef={titleInputRef}
          contentTextareaRef={contentTextareaRef}
          promptTextareaRef={promptTextareaRef}
          onWeaveNavigation={handleWeaveNavigation}
          isSaving={isSaving}
          lastSaved={lastSaved}
          slotAttachedContent={slotAttachedContent}
          showWritingSupport={showWritingSupport}
          setShowWritingSupport={setShowWritingSupport}
          // Essay slot system props
          selectedCollegeId={selectedCollegeId}
          setSelectedCollegeId={setSelectedCollegeId}
          availableSlots={availableSlots}
          loadingSlots={loadingSlots}
          slotDrafts={slotDrafts}
          loadSlotDrafts={loadSlotDrafts}
          essays={essays}
          onSelectEssay={onSelectEssay}
          onCreateEssay={onCreateEssay}
          onUpdateEssay={onUpdateEssay}
        />
      ) : (
        // Default fallback - shouldn't normally reach here
        <div className="p-8 text-center text-stone-500">
          Unknown mode: {mode}
        </div>
      )}

      {/* Strategic Word Count Modal */}
      {showStrategicWordCount && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            <StrategicWordCountPanel
              essay={currentEssay}
              essayContent={content}
              onClose={() => setShowStrategicWordCount(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EssayDevelopmentInterface;